import { Grid, Switch } from '@material-ui/core';
import { cloneDeep } from 'lodash-es';
import React, { useEffect, useState } from 'react';

import {
  getEPGContentTogglesForPublisher,
  updateEPGContentTogglesForPublisher,
} from '~/src/dash/api/demand-settings/epg/epg_methods';
import { IContentFromEPG } from '~/src/dash/api/demand-settings/epg/epg_models';
import { EBool } from '~/src/dash/api/types/bool';
import { IAccordionPanel, MultiAccordion } from '~/src/dash/components/accordion/multi-accordion';
import { FocusedTextfield } from '~/src/dash/components/focused-textfield';
import { InfoTooltipIcon } from '~/src/dash/components/icons/InfoIcon';
import { IInputRow } from '~/src/dash/components/input-row';
import { Text } from '~/src/dash/components/text';
import { Col, Cols, Row, Rows } from '~/src/dash/publicaUI';
import { IStore } from '~/src/dash/reducers/store';
import {
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { decorate } from '~/src/dash/utils/decorator';
import { multiIndexBidders } from '~/src/dash/utils/header-bidder-adapters';
import {
  errorColor,
  growGrid,
  growGridLeft,
  growGridRight,
} from '~/src/dash/utils/material-dashboard-react';
import { hasRoleSuperUser } from '~/src/dash/utils/roles';
import { isSamsungPublisher } from '~/src/dash/utils/ui';
import { SnackbarV2 } from '~/src/dash/views/snackbar-view-v2';

interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {
  setSaveFunc: (handleSave: () => void) => {};
  demandSource: string;
}

const _IDemandSettingsEPG = (props: IProps) => {
  const [contentToggles, setContentToggles] = useState<IContentFromEPG[]>([]);
  const [overrideEnabled, setOverrideEnabled] = useState<EBool>(EBool.EBool_FALSE);

  const snackbarV2 = new SnackbarV2();

  const getEPGContentToggles = () => {
    getEPGContentTogglesForPublisher(props.session.publisher.ID).then(([_EPGToggles, err]) => {
      if (err) {
        console.error(err);
        snackbarV2.displayMessage(err.message, true);
      } else if (_EPGToggles) {
        setContentToggles((prevContentToggle) => {
          const clonedPrevContentToggle = cloneDeep(prevContentToggle);
          clonedPrevContentToggle.push(..._EPGToggles);
          return clonedPrevContentToggle;
        });
      }
    });
  };

  const saveValue = (
    value: number,
    field:
      | 'EnableRemoveGPPFromBidRequest'
      | 'ContentFromEPGEnabled'
      | 'BidRequestTimeout'
      | 'UID2PassthroughEnabled'
      | 'GooglePairIdEnabled'
      | 'LiverampEnvelopeEnabled'
      | 'SamsungIdentityEnabled',
  ) => {
    const newContentToggles = cloneDeep(contentToggles);

    const foundContentToggles = newContentToggles.find((element) => {
      return element.DemandSource === props.demandSource;
    });

    if (foundContentToggles) {
      if (field === 'ContentFromEPGEnabled') {
        foundContentToggles.ContentFromEPGEnabled = value;
      } else if (field === 'EnableRemoveGPPFromBidRequest') {
        foundContentToggles.EnableRemoveGPPFromBidRequest = value;
      } else if (field === 'UID2PassthroughEnabled') {
        foundContentToggles.UID2PassthroughEnabled = value;
      } else if (field === 'GooglePairIdEnabled') {
        foundContentToggles.GooglePairIdEnabled = value;
      } else if (field === 'LiverampEnvelopeEnabled') {
        foundContentToggles.LiverampEnvelopeEnabled = value;
      } else if (field === 'SamsungIdentityEnabled') {
        foundContentToggles.SamsungIdentityEnabled = value;
      } else if (field === 'BidRequestTimeout') {
        if (value < 0 || isNaN(value)) {
          foundContentToggles.BidRequestTimeout = 0;
        } else {
          foundContentToggles.BidRequestTimeout = value;
        }
      }
      setContentToggles(newContentToggles);
    } else {
      const newContentToggle = {
        ID: 0,
        PublisherID: parseInt(props.session.publisher.ID, 10),
        DemandSource: props.demandSource,
        BidRequestTimeout: 0,
        EnableRemoveGPPFromBidRequest: EBool.EBool_FALSE,
        ContentFromEPGEnabled:
          field === 'ContentFromEPGEnabled' ? EBool.EBool_TRUE : EBool.EBool_FALSE,
        ContentChannelOverrideFromEPG: EBool.EBool_FALSE,
        ContentChannelOverrideIfEmpty: EBool.EBool_FALSE,
        ContentNetworkOverrideFromEPG: EBool.EBool_FALSE,
        ContentNetworkOverrideIfEmpty: EBool.EBool_FALSE,
        ContentSeriesOverrideFromEPG: EBool.EBool_FALSE,
        ContentSeriesOverrideIfEmpty: EBool.EBool_FALSE,
        ContentTitleOverrideFromEPG: EBool.EBool_FALSE,
        ContentTitleOverrideIfEmpty: EBool.EBool_FALSE,
        ContentGenreOverrideFromEPG: EBool.EBool_FALSE,
        ContentGenreOverrideIfEmpty: EBool.EBool_FALSE,
        ContentRatingOverrideFromEPG: EBool.EBool_FALSE,
        ContentRatingOverrideIfEmpty: EBool.EBool_FALSE,
        ContentSubRatingOverrideFromEPG: EBool.EBool_FALSE,
        ContentSubRatingOverrideIfEmpty: EBool.EBool_FALSE,
        UID2PassthroughEnabled: EBool.EBool_FALSE,
        GooglePairIdEnabled: EBool.EBool_FALSE,
        LiverampEnvelopeEnabled: EBool.EBool_FALSE,
        SamsungIdentityEnabled: EBool.EBool_FALSE,
      };
      if (field === 'ContentFromEPGEnabled') {
        newContentToggle.ContentFromEPGEnabled = value;
      } else if (field === 'EnableRemoveGPPFromBidRequest') {
        newContentToggle.EnableRemoveGPPFromBidRequest = value;
      } else if (field === 'UID2PassthroughEnabled') {
        newContentToggle.UID2PassthroughEnabled = value;
      } else if (field === 'GooglePairIdEnabled') {
        newContentToggle.GooglePairIdEnabled = value;
      } else if (field === 'LiverampEnvelopeEnabled') {
        newContentToggle.LiverampEnvelopeEnabled = value;
      } else if (field === 'SamsungIdentityEnabled') {
        newContentToggle.SamsungIdentityEnabled = value;
      } else if (field === 'BidRequestTimeout') {
        newContentToggle.BidRequestTimeout = value;
      }
      newContentToggles.push(newContentToggle);
      setContentToggles(newContentToggles);
      setOverrideEnabled(EBool.reverse(overrideEnabled));
    }
  };

  useEffect(() => {
    getEPGContentToggles();
  }, []);

  const handleToggleChange = (key: string, isPassValueFromEPG: boolean) => {
    if (isPassValueFromEPG) {
      const newContentToggles = contentToggles;
      newContentToggles.map((element) => {
        if (element.DemandSource === props.demandSource) {
          element[`${key}`] = EBool.reverse(element[`${key}`]);
        }
      });
      setContentToggles(newContentToggles);
      setOverrideEnabled(EBool.reverse(overrideEnabled));
    } else {
      snackbarV2.displayMessage("Please toggle 'Pass Value From EPG' on first");
    }
  };

  const handleSave = () => {
    updateEPGContentTogglesForPublisher(props.session.publisher.ID, contentToggles).then(
      ([_toggles, err]) => {
        if (err) {
          console.error(err);
          snackbarV2.displayMessage(err.message, true);
        } else if (_toggles) {
          setContentToggles(_toggles);
          snackbarV2.displayMessage('Content overrides setting saved');
        }
      },
    );
  };

  props.setSaveFunc(handleSave);

  const contentTogglesFromDemandSource = contentToggles.find(
    (element) => element.DemandSource === props.demandSource,
  );

  const renderBidRequestRewrite = () => {
    const ContentOverrideRows = [
      'Channel',
      // we currently do not have network or subrating information from EPG Channel Info
      // EPGChannelInfo.NetworkCallSign is currently assigend to ContentChannel
      // 'Network',
      'Series',
      'Title',
      'Genre',
      'Rating',
      // 'SubRating',
    ].map((contentItem: string) => {
      const contentTitle = `Content ${contentItem} Override`;
      const contentField = `Content${contentItem}OverrideFromEPG`;
      const contentFieldIfEmpty = `Content${contentItem}OverrideIfEmpty`;
      let contentValue = EBool.EBool_FALSE;
      let contentValueIfEmpty = EBool.EBool_FALSE;
      if (contentTogglesFromDemandSource) {
        contentValue = contentTogglesFromDemandSource[contentField];
        contentValueIfEmpty = contentTogglesFromDemandSource[contentFieldIfEmpty];
      }

      return {
        renderType: 'DoubleSwitch',
        title3: contentTitle,
        title1: 'Pass Value from EPG',
        value1: contentValue,
        defaultValue1: EBool.EBool_FALSE,
        isActiveValue1: () => false,
        getActiveTag1: () => '',
        handleChange1: () => handleToggleChange(contentField, true),

        title2: `Add value only if obj. empty in endpoint request`,
        value2: contentValue === EBool.EBool_TRUE ? contentValueIfEmpty : EBool.EBool_FALSE,
        defaultValue2: EBool.EBool_FALSE,
        handleChange2: () =>
          handleToggleChange(contentFieldIfEmpty, contentValue === EBool.EBool_TRUE),
      } as IInputRow;
    });

    const ContentOverridePanel = {
      title: 'Content',
      panelActiveChipTitle: ' ',
      panelInactiveRowTag: ' ',
      rows: [...ContentOverrideRows],
      forcePanelsOpen: true,
    } as IAccordionPanel;

    // define the rows and then the panel to which they belong
    const panels = [ContentOverridePanel];
    return (
      <Row>
        <Grid item style={{ marginBottom: 10, maxWidth: 1200, paddingLeft: 28 }}>
          <Grid container direction='column'>
            <MultiAccordion
              title='ORTB Objects'
              subtitle='Set which ORTB objects below should be rewritten, altered or removed from requests before reaching this bidder'
              panels={panels}
              disableEdit={false}
              forcePanelsOpen={true}
              endpointScorecard={true}
            />
          </Grid>
        </Grid>
      </Row>
    );
  };

  const isFieldEnabled = (field: string): boolean => {
    if (!contentTogglesFromDemandSource) {
      return false;
    }
    switch (field) {
      case 'ContentFromEPGEnabled':
        return EBool.toBool(contentTogglesFromDemandSource.ContentFromEPGEnabled);
      case 'EnableRemoveGPPFromBidRequest':
        return EBool.toBool(contentTogglesFromDemandSource.EnableRemoveGPPFromBidRequest);
      case 'UID2PassthroughEnabled':
        return EBool.toBool(contentTogglesFromDemandSource.UID2PassthroughEnabled);
      case 'GooglePairIdEnabled':
        return EBool.toBool(contentTogglesFromDemandSource.GooglePairIdEnabled);
      case 'LiverampEnvelopeEnabled':
        // Liveramp Envelope is also called LiveRamp ATS and IDL
        return EBool.toBool(contentTogglesFromDemandSource.LiverampEnvelopeEnabled);
      case 'SamsungIdentityEnabled':
        return EBool.toBool(contentTogglesFromDemandSource.SamsungIdentityEnabled);
      default:
        return false;
    }
  };

  return (
    <Rows>
      {hasRoleSuperUser(props.user.user) && (
        <Row>
          <Cols style={{ alignItems: 'center' }}>
            <Col paddingRight='20px'>
              <Text bold>Remove GPP From Bid Request *</Text>
            </Col>
            <Col>
              {isFieldEnabled('EnableRemoveGPPFromBidRequest') ? 'Active' : 'Inactive'}
              <Switch
                name='Remove GPP From Bid Request'
                checked={isFieldEnabled('EnableRemoveGPPFromBidRequest')}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  saveValue(EBool.fromBool(event.target.checked), 'EnableRemoveGPPFromBidRequest');
                }}
              />
            </Col>
          </Cols>
        </Row>
      )}
      {!multiIndexBidders.includes(props.demandSource) && hasRoleSuperUser(props.user.user) && (
        <Row>
          <Cols style={{ alignItems: 'center' }}>
            <Col paddingRight='20px'>
              <Text bold>Bid Request Timeout *</Text>
            </Col>
            <Col paddingRight='10px'>
              <FocusedTextfield
                type='number'
                multiline
                label=''
                name='BidRequestTimeout'
                value={
                  contentTogglesFromDemandSource
                    ? contentTogglesFromDemandSource.BidRequestTimeout
                    : 0
                }
                onChange={(e) => saveValue(parseInt(e.target.value, 10), 'BidRequestTimeout')}
              />
            </Col>
            <Col>
              <InfoTooltipIcon
                tooltipText={
                  'Sets timeout for all bid requests from this demand source (0 will use channel/default setting)'
                }
              />
            </Col>
          </Cols>
        </Row>
      )}
      <Row>
        <Cols style={{ alignItems: 'center' }}>
          <Col paddingRight='20px'>
            <Text bold>Enable UID 2.0 Passthrough</Text>
          </Col>
          <Col>
            {isFieldEnabled('UID2PassthroughEnabled') ? 'Active' : 'Inactive'}
            <Switch
              name='Enable UID 2.0'
              checked={isFieldEnabled('UID2PassthroughEnabled')}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                saveValue(EBool.fromBool(event.target.checked), 'UID2PassthroughEnabled');
              }}
            />
          </Col>
          <Col>
            <InfoTooltipIcon
              tooltipText={
                'This will allow UID 2.0 params to pass through to the bid stream for selected demand source'
              }
            />
          </Col>
        </Cols>
      </Row>
      <Row>
        <Cols style={{ alignItems: 'center' }}>
          <Col paddingRight='20px'>
            <Text bold>Enable Google PAIR ID Passthrough</Text>
          </Col>
          <Col>
            {isFieldEnabled('GooglePairIdEnabled') ? 'Active' : 'Inactive'}
            <Switch
              name='Enable Google PAIR ID'
              checked={isFieldEnabled('GooglePairIdEnabled')}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                saveValue(EBool.fromBool(event.target.checked), 'GooglePairIdEnabled');
              }}
            />
          </Col>
          <Col>
            <InfoTooltipIcon
              tooltipText={
                'This will allow Google Pair ID to passthrough on the bid request to the selected Demand Source. ID comes in on the endpoint request.'
              }
            />
          </Col>
        </Cols>
      </Row>
      <Row>
        <Cols style={{ alignItems: 'center' }}>
          <Col paddingRight='20px'>
            <Text bold>Enable LiveRamp ATS Passthrough</Text>
          </Col>
          <Col>
            {isFieldEnabled('LiverampEnvelopeEnabled') ? 'Active' : 'Inactive'}
            <Switch
              name='Enable Liveramp Envelope'
              checked={isFieldEnabled('LiverampEnvelopeEnabled')}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                saveValue(EBool.fromBool(event.target.checked), 'LiverampEnvelopeEnabled');
              }}
            />
          </Col>
          <Col>
            <InfoTooltipIcon
              tooltipText={
                'This will allow Liveramp ATS value to pass through in the user.ext.eids object of the bid request to the selected Demand Source.'
              }
            />
          </Col>
        </Cols>
      </Row>
      {isSamsungPublisher(props.session.publisher.ID) && (
        <Row>
          <Cols style={{ alignItems: 'center' }}>
            <Col paddingRight='20px'>
              <Text bold>Enable Samsung Identity</Text>
            </Col>
            <Col>
              {isFieldEnabled('SamsungIdentityEnabled') ? 'Active' : 'Inactive'}
              <Switch
                name='Enable Samsung Identity'
                checked={isFieldEnabled('SamsungIdentityEnabled')}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  saveValue(EBool.fromBool(event.target.checked), 'SamsungIdentityEnabled');
                }}
              />
            </Col>
            <Col>
              <InfoTooltipIcon
                tooltipText={
                  'This will allow Pair ID and Ramp ID to pass through in the user.ext.eids object of the bid request to the selected Demand Source.'
                }
              />
            </Col>
          </Cols>
        </Row>
      )}
      <Row>
        <Cols style={{ alignItems: 'center' }}>
          <Col paddingRight='20px'>
            <Text bold>Content from EPG</Text>
          </Col>
          <Col>
            {isFieldEnabled('ContentFromEPGEnabled') ? 'Active' : 'Inactive'}
            <Switch
              name='Enable Content from EPG'
              checked={isFieldEnabled('ContentFromEPGEnabled')}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                saveValue(EBool.fromBool(event.target.checked), 'ContentFromEPGEnabled');
              }}
            />
          </Col>
        </Cols>
      </Row>
      {isFieldEnabled('ContentFromEPGEnabled') && renderBidRequestRewrite()}
      {snackbarV2.component()}
    </Rows>
  );
};

export const DemandSettingsEPG = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    styles: (theme: any) => ({
      paper: {
        ...theme.mixins.gutters(),
        paddingTop: theme.spacing.unit * 2,
        paddingBottom: theme.spacing.unit * 2,
        marginBottom: theme.spacing.unit * 4,
      },
      section: {
        display: 'block',
        marginBottom: theme.spacing.unit * 4,
      },
      button: {
        margin: theme.spacing.unit,
      },
      growGrid,
      growGridLeft,
      growGridRight,
      errorColor: {
        color: errorColor,
      },
      clampTableCell: {
        whiteSpace: 'nowrap',
        width: '1px',
      },
    }),
    router: true,
  },
  _IDemandSettingsEPG,
);
